"use client";

import SwiperButton from "@repo/shared-ui/SwiperButton";
import React, { FC, useCallback, useRef, useState } from "react";
import type { Swiper as SwiperType } from "swiper";
import { A11y, Navigation, Scrollbar } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import TrendingStockCard from "./components/TrendingStockCard";
import type {
  TrendingStocksAemConfig,
  TrendingStocksCardData,
  TrendingStocksPrimaryTab,
  TrendingStocksSelectionKey,
  TrendingStockTradeAction,
} from "./types";

interface TrendingStocksCarouselPanelProps {
  isActive: boolean;
  items: TrendingStocksCardData[];
  selectionKey: TrendingStocksSelectionKey;
  tab: TrendingStocksPrimaryTab;
  config: TrendingStocksAemConfig;
  onTradeClick: (
    action: TrendingStockTradeAction,
    stock: TrendingStocksCardData,
  ) => void | Promise<void>;
}

const TrendingStocksCarouselPanel: FC<TrendingStocksCarouselPanelProps> = ({
  isActive,
  items,
  selectionKey,
  tab,
  config,
  onTradeClick,
}) => {
  const [isAtStart, setIsAtStart] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(false);
  const prevRef = useRef<HTMLButtonElement | null>(null);
  const nextRef = useRef<HTMLButtonElement | null>(null);

  const handleBeforeInit = useCallback((swiper: SwiperType) => {
    if (typeof swiper.params.navigation !== "boolean" && swiper.params.navigation) {
      swiper.params.navigation.prevEl = prevRef.current;
      swiper.params.navigation.nextEl = nextRef.current;
    }
  }, []);

  const handleSwiperUpdate = useCallback((swiper: SwiperType) => {
    setIsAtStart(swiper.isBeginning);
    setIsAtEnd(swiper.isEnd);
  }, []);

  if (items.length === 0) return null;

  return (
    <div
      className={`relative ${isActive ? "" : "hidden"}`}
      data-tab={tab}
    >
      <SwiperButton ref={prevRef} direction="prev" isDisabled={isAtStart} />
      <SwiperButton ref={nextRef} direction="next" isDisabled={isAtEnd} />
      <Swiper
        modules={[Navigation, Scrollbar, A11y]}
        spaceBetween={12}
        slidesPerView="auto"
        allowTouchMove
        observer
        observeParents
        navigation={{
          prevEl: prevRef.current,
          nextEl: nextRef.current,
        }}
        onBeforeInit={handleBeforeInit}
        onInit={handleSwiperUpdate}
        onSlideChange={handleSwiperUpdate}
        onResize={handleSwiperUpdate}
        scrollbar={{ draggable: false }}
      >
        {/* md slide width matches TrendingStockCard carousel max-w to avoid empty gutter between cards */}
        {items.map((stock, index) => (
          <SwiperSlide
            key={`${selectionKey}-${stock.id}-${index}`}
            className="w-[calc(100vw-32px)]! min-w-[310px]! max-w-[310px]! md:min-w-[550px]! md:max-w-[550px]!"
          >
            <TrendingStockCard
              variant="carousel"
              stock={stock}
              fieldLabels={config.fields}
              watchlist={config.watchlist}
              buyText={config.trade?.buyText ?? "BUY"}
              sellText={config.trade?.sellText ?? "SELL"}
              onTradeClick={onTradeClick}
            />
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
};

export default TrendingStocksCarouselPanel;
